import { AsyncPipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { GuardsCheckEnd, GuardsCheckStart, NavigationCancel, Router, RouterOutlet } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { filter, map, Observable, startWith, Subject, takeUntil } from 'rxjs';
import { ProfileService } from './auth/services/profile.service';
import { AlertComponent } from './shared/components/alert/alert.component';
import { CookiesComponent } from './shared/components/cookies/cookies.component';
import { HeaderComponent } from './shared/components/header/header.component';
import { IndicatorComponent } from './shared/components/indicator/indicator.component';
import { MenuComponent } from './shared/components/menu/menu/menu.component';
import { SpinnerComponent } from './shared/components/spinner/spinner.component';
import { UpdateComponent } from './shared/components/update/update.component';
import { DesktopNotificationService } from './shared/services/desktop-notification-service/desktop-notification.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  imports: [
    HeaderComponent,
    AlertComponent,
    RouterOutlet,
    AsyncPipe,
    SpinnerComponent,
    IndicatorComponent,
    UpdateComponent,
    MenuComponent,
    CookiesComponent,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  protected loading$: Observable<boolean>;
  protected showUpdateBar: boolean = false;
  private readonly destroying$ = new Subject<void>();

  private readonly authService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly desktopNotificationService = inject(DesktopNotificationService);
  private readonly profileService = inject(ProfileService);

  constructor() {
    const router = inject(Router);
    this.loading$ = router.events.pipe(
      filter(
        (event) =>
          event instanceof GuardsCheckStart || event instanceof GuardsCheckEnd || event instanceof NavigationCancel,
      ),
      map((event) => event instanceof GuardsCheckStart),
      startWith(true),
    );
  }

  async ngOnInit(): Promise<void> {
    this.authService.handleRedirectObservable().subscribe();

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS,
        ),
        takeUntil(this.destroying$),
      )
      .subscribe(async (result) => {
        if (result.eventType === EventType.LOGIN_SUCCESS) {
          await this.desktopNotificationService.initPushNotifications();
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this.destroying$),
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
        this.profileService.reloadProfile();
      });
  }

  ngOnDestroy(): void {
    this.destroying$.next(undefined);
    this.destroying$.complete();
  }

  private checkAndSetActiveAccount(): void {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    const activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      const accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }
}
